import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api-client';

/**
 * In-app notifications — reads `notifications_outbox` rows targeted at
 * the signed-in user (either directly by user_id or via their role).
 *
 * Producers: backend declaration / proof / window endpoints + future
 * payroll-run / FnF / approval workflows. Consumers: Topbar bell icon
 * (poll every 30s) and a future `/notifications` full-page view.
 */

export type Notification = {
  id: string;
  kind: string;
  subject: string;
  body: string;
  link_path: string | null;
  channels: string;
  status: 'PENDING' | 'READ' | 'SENT' | 'FAILED';
  created_at: string | null;
  read_at: string | null;
  recipient_user_id: string | null;
  recipient_role: string | null;
};

export type NotificationsResp = {
  items: Notification[];
  unread_count: number;
  /** Rows matching the status / kind filter, across all pages. */
  total?: number;
  offset?: number;
  limit?: number;
};

/** One page of the signed-in user's notifications, newest first — the
 *  history page. Their own rows plus their role's, exactly like the bell. */
export function useNotificationHistory(args: {
  status: 'UNREAD' | 'READ' | 'ALL'; page: number; pageSize: number; kind?: string;
}) {
  return useQuery({
    queryKey: ['notifications', 'history', args],
    queryFn: () => api.get<NotificationsResp>('/notifications', {
      status: args.status,
      kind: args.kind || undefined,
      limit: args.pageSize,
      offset: args.page * args.pageSize,
    }),
    placeholderData: (prev) => prev,
  });
}

export function useNotifications(
  args: { user_id?: string | null; role?: string | null; status?: 'UNREAD' | 'READ' | 'ALL' } = {},
  /** Polling interval in ms — keep low so the bell badge feels live without
   *  hammering the API. Pass 0 to disable polling (e.g., when the popover
   *  is closed). */
  pollMs = 30_000,
) {
  return useQuery({
    queryKey: ['notifications', args],
    queryFn: () => api.get<NotificationsResp>('/notifications', {
      user_id: args.user_id || undefined,
      role:    args.role    || undefined,
      status:  args.status  || 'UNREAD',
      limit:   50,
    }),
    enabled: !!(args.user_id || args.role),
    refetchInterval: pollMs || false,
  });
}

export function useMarkNotificationRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api.post<void>(`/notifications/${id}/mark-read`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notifications'] }),
  });
}

export function useMarkAllNotificationsRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { user_id?: string | null; role?: string | null }) => {
      // api.post doesn't take query params, so encode them inline.
      const qs = new URLSearchParams();
      if (args.user_id) qs.set('user_id', args.user_id);
      if (args.role)    qs.set('role', args.role);
      const tail = qs.toString() ? `?${qs.toString()}` : '';
      return api.post<void>(`/notifications/mark-all-read${tail}`, {});
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notifications'] }),
  });
}
